import type { PoolShapeType } from "models/Project";

export type RDMResult = "YES" | "NO" | "DOUBLE";

type PoolShapeTypeValue = (typeof PoolShapeType)[number]["value"];

export interface DetermineReducedDeckMountParams {
  /** Pool shape configuration with type information */
  poolShapeType: PoolShapeTypeValue | null | undefined;
  /** Cover area in square feet (from PoolMeasurements.coverAreaSqFt) */
  coverAreaSqFt: number | null | undefined;
  /** Cover overlap in inches (from PoolMeasurements.coverOverlapIn) - typically 12, 18, or 24 */
  coverOverlapIn: number | null | undefined;
  /** Distance from pool perimeter in inches (from FeatureMeasurements.distanceFromPerimeterIn) */
  distanceFromPerimeterIn: number | null | undefined;
  /** Whether there is a raised wall behind the deck (affects RDM determination for certain ranges) */
  hasRaisedWallBehindDeck?: boolean;
}

export const determineReducedDeckMount = (
  params: DetermineReducedDeckMountParams,
): boolean => {
  const {
    poolShapeType,
    coverAreaSqFt,
    coverOverlapIn,
    distanceFromPerimeterIn,
    hasRaisedWallBehindDeck,
  } = params;

  // Early return for missing critical data
  // If feature type is not spa or wall, we know that there is no raised wall or spa behind the reduced deck mount
  if (!poolShapeType || !coverAreaSqFt || !coverOverlapIn || !distanceFromPerimeterIn) {
    return false;
  }

  // Returns RDMResult based on pool characteristics
  const automatedToggleResults = resolveSuggestedRDMResult({
    poolShapeType,
    coverOverlapIn,
    coverAreaSqFt,
    distanceFromPerimeterIn,
    hasRaisedWallBehindDeck,
  });

  // Return boolean value based on toggle input value
  return Boolean(
    automatedToggleResults === "YES" || automatedToggleResults === "DOUBLE",
  );
};

// suggestion based on pool characteristics
export function resolveSuggestedRDMResult({
  poolShapeType,
  coverOverlapIn,
  coverAreaSqFt,
  distanceFromPerimeterIn,
  hasRaisedWallBehindDeck,
}: {
  poolShapeType: PoolShapeTypeValue;
  coverOverlapIn: number;
  coverAreaSqFt: number;
  distanceFromPerimeterIn: number;
  hasRaisedWallBehindDeck: boolean | undefined;
}): "YES" | "NO" | "DOUBLE" {
  // === GEOMETRIC CRITERIA ===
  if (poolShapeType === "geometric") {
    const isAreaSquareFeet1To2099 =
      coverAreaSqFt >= 1 &&
      coverAreaSqFt <= 2099 &&
      coverOverlapIn >= 0 &&
      coverOverlapIn <= 12;
    const isAreaSquareFeet2100To3500 =
      coverAreaSqFt >= 2100 &&
      coverAreaSqFt <= 3500 &&
      coverOverlapIn >= 13 &&
      coverOverlapIn <= 18;
    const isAreaSquareFeet3501To4000 =
      coverAreaSqFt >= 3501 &&
      coverAreaSqFt <= 4000 &&
      coverOverlapIn >= 19 &&
      coverOverlapIn <= 24;

    // === 1-2099 sqft, 12" overlap ===
    if (isAreaSquareFeet1To2099) {
      // 12-15" With Raised Wall Behind Deck
      if (
        hasRaisedWallBehindDeck &&
        distanceFromPerimeterIn >= 12 &&
        distanceFromPerimeterIn <= 15
      ) {
        return "DOUBLE";
      }
      // 16-20" With Raised Wall Behind Deck
      if (
        hasRaisedWallBehindDeck &&
        distanceFromPerimeterIn >= 16 &&
        distanceFromPerimeterIn <= 20
      ) {
        return "YES";
      }
      // 0-15"
      if (distanceFromPerimeterIn >= 0 && distanceFromPerimeterIn <= 15) {
        return "NO";
      }
      // 16-20"
      if (distanceFromPerimeterIn >= 16 && distanceFromPerimeterIn <= 20) {
        return "DOUBLE";
      }
      // 21-26"
      if (distanceFromPerimeterIn >= 21 && distanceFromPerimeterIn <= 26) {
        return "YES";
      }
      // 27-36"
      if (distanceFromPerimeterIn >= 27 && distanceFromPerimeterIn <= 36) {
        return "NO";
      }
    }

    // === 2100-3500 sqft, 18" overlap ===
    // === 3501-4000 sqft, 24" overlap ===
    if (isAreaSquareFeet2100To3500 || isAreaSquareFeet3501To4000) {
      // 12-15" With Raised Wall Behind Deck
      if (
        hasRaisedWallBehindDeck &&
        distanceFromPerimeterIn >= 12 &&
        distanceFromPerimeterIn <= 15
      ) {
        return "DOUBLE";
      }
      // 16-24" With Raised Wall Behind Deck
      if (
        hasRaisedWallBehindDeck &&
        distanceFromPerimeterIn >= 16 &&
        distanceFromPerimeterIn <= 24
      ) {
        return "YES";
      }
      // 0-15"
      if (distanceFromPerimeterIn >= 0 && distanceFromPerimeterIn <= 15) {
        return "NO";
      }
      // 16-24"
      if (distanceFromPerimeterIn >= 16 && distanceFromPerimeterIn <= 24) {
        return "DOUBLE";
      }
      // 25-32"
      if (distanceFromPerimeterIn >= 25 && distanceFromPerimeterIn <= 32) {
        return "YES";
      }
      // 33-36"
      if (distanceFromPerimeterIn >= 33 && distanceFromPerimeterIn <= 36) {
        return "NO";
      }
    }
  }

  // === FREEFORM CRITERIA ===
  if (poolShapeType === "freeform") {
    const isAreaSquareFeet1To3500 =
      coverAreaSqFt >= 1 &&
      coverAreaSqFt <= 3500 &&
      coverOverlapIn >= 0 &&
      coverOverlapIn <= 18;
    const isAreaSquareFeet3501To4000 =
      coverAreaSqFt >= 3501 &&
      coverAreaSqFt <= 4000 &&
      coverOverlapIn >= 19 &&
      coverOverlapIn <= 24;

    if (isAreaSquareFeet1To3500 || isAreaSquareFeet3501To4000) {
      // 12-15" With Raised Wall Behind Deck
      if (
        hasRaisedWallBehindDeck &&
        distanceFromPerimeterIn >= 12 &&
        distanceFromPerimeterIn <= 15
      ) {
        return "DOUBLE";
      }
      // 16-24" With Raised Wall Behind Deck
      if (
        hasRaisedWallBehindDeck &&
        distanceFromPerimeterIn >= 16 &&
        distanceFromPerimeterIn <= 24
      ) {
        return "YES";
      }
      // 0-15"
      if (distanceFromPerimeterIn >= 0 && distanceFromPerimeterIn <= 15) {
        return "NO";
      }
      // 16-24"
      if (distanceFromPerimeterIn >= 16 && distanceFromPerimeterIn <= 24) {
        return "DOUBLE";
      }
      // 25-32"
      if (distanceFromPerimeterIn >= 25 && distanceFromPerimeterIn <= 32) {
        return "YES";
      }
      // 33-36"
      if (distanceFromPerimeterIn >= 33 && distanceFromPerimeterIn <= 36) {
        return "NO";
      }
    }
  }

  // === NO MATCH ===
  // Alwayys returns NO as default
  return "NO";
}
